import React, { useMemo } from 'react';
import { AlertCircle } from 'lucide-react';

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  error?: string;
  label?: string;
  required?: boolean;
}

// External value format is dd/mm/yyyy (Spanish locale, used across the app and DB).
// Native <input type="date"> uses yyyy-mm-dd. We convert in/out so callers don't change.
const ddmmyyyyToIso = (value: string): string => {
  if (!value) return '';
  const m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return '';
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
};

const isoToDdmmyyyy = (value: string): string => {
  if (!value) return '';
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, yyyy, mm, dd] = m;
  return `${dd}/${mm}/${yyyy}`;
};

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  error,
  label = 'Fecha de Nacimiento',
  required = false,
}) => {
  const isoValue = useMemo(() => ddmmyyyyToIso(value), [value]);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <label className="block text-sm font-medium text-stone-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>

      <input
        type="date"
        value={isoValue}
        onChange={(e) => onChange(isoToDdmmyyyy(e.target.value))}
        max={today}
        min="1850-01-01"
        required={required}
        className={`w-full h-11 px-3 text-sm bg-white border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 ${
          error ? 'border-rose-500' : 'border-stone-300'
        }`}
      />

      {error && (
        <p className="mt-1 text-xs text-rose-600 flex items-center gap-1" role="alert">
          <AlertCircle className="h-3 w-3 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export default DatePicker;
